import { OptimizePlanRequest, OptimizePlanResponse } from "@/api";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Slider } from "primereact/slider";
import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { excelPlanResponseAtom } from "@/atoms/optimization";

interface CapacitySettingProps {
  originalResponse: OptimizePlanResponse;
  optimizeRequest: OptimizePlanRequest;
  holidayDates?: string[]; // 一斉休業日のリスト
  onBack(): void;
  onProceed(request: OptimizePlanRequest): void;
}

const BASE_HOURS = 8; // 基準時間（時間換算）

const CapacitySetting = ({ originalResponse, optimizeRequest, holidayDates = [], onBack, onProceed }: CapacitySettingProps) => {
  const excelPlanResponse = useAtomValue(excelPlanResponseAtom);

  const echoProducts = excelPlanResponse?.echoProducts ?? [];

  const machineNames: string[] = useMemo(() => {
    const names = echoProducts
      .flatMap((p) => Object.values(p.machine_variants ?? {}))
      .map((v) => v.machine_id)
      .filter((v): v is string => !!v)
      .map((v) => v.trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [echoProducts]);

  const [machineEfficiency, setMachineEfficiency] = useState<Record<string, number>>({});

  useEffect(() => {
    if (machineNames.length === 0) {
      setMachineEfficiency({});
      return;
    }
    setMachineEfficiency((prev) => {
      const next: Record<string, number> = {};
      machineNames.forEach((m) => {
        next[m] = prev[m] ?? 1.0;
      });
      return next;
    });
  }, [machineNames]);

  const handleEfficiencyChange = (machine: string, value: number | null) => {
    if (value === null) return;
    setMachineEfficiency((prev) => ({ ...prev, [machine]: value }));
  };

  const applyBulkEfficiency = (value: number) => {
    const next: Record<string, number> = {};
    machineNames.forEach((m) => {
      next[m] = value;
    });
    setMachineEfficiency(next);
  };

  const handleBack = () => {
    onBack();
  };

  const handleProceed = () => {
    const effectiveLines = machineNames.length ? machineNames : optimizeRequest.days[0]?.capacity.lines ?? [];
    const avgEfficiency = machineNames.length
      ? machineNames.reduce((sum, m) => sum + (machineEfficiency[m] ?? 1), 0) / machineNames.length
      : 1;

    const updatedRequest: OptimizePlanRequest = {
      ...optimizeRequest,
      days: optimizeRequest.days.map((day, index) => ({
        ...day,
        capacity: {
          capacity: { value: excelPlanResponse?.holidayDates.includes(day.date) ? 0 : 24 },
          lines: [...effectiveLines],
          machineEfficiency: { ...machineEfficiency },
          amount: Math.round(
            typeof day.capacity.amount === "number"
              ? day.capacity.amount * avgEfficiency
              : 3000 * avgEfficiency
          ),
        },
      })),
    };
    onProceed(updatedRequest);
  };

  return (
    <div className="mt-6">
      <h3>成型機稼働設定</h3>
      <p className="text-sm text-600 mb-4">
        各成型機の稼働率を設定してください。設定した稼働率の平均を日別容量に反映します。
      </p>

      <div className="flex gap-2 mb-4">
        <Button label="全100%" size="small" severity="secondary" onClick={() => applyBulkEfficiency(1.0)} />
        <Button label="全75%" size="small" severity="secondary" onClick={() => applyBulkEfficiency(0.75)} />
        <Button label="全50%" size="small" severity="secondary" onClick={() => applyBulkEfficiency(0.5)} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {machineNames.length === 0 && (
          <p className="text-sm text-500">成型機情報が見つかりませんでした（echoProducts の machine_sheet を確認してください）。</p>
        )}
        {machineNames.map((machine) => {
          const eff = machineEfficiency[machine] ?? 1;
          const percent = Math.round((eff ?? 0) * 100);
          return (
            <div key={machine} className="p-3 border-round surface-card shadow-1 flex flex-col gap-3">
              <div className="flex items-center justify-between tw-min-w-[220px]">
                <div>
                  <div className="font-medium text-lg">{machine}</div>
                  <div className="text-500 text-sm">稼働率 (0% - 100%)</div>
                </div>
                <div className="text-lg font-semibold">{percent}%</div>
              </div>
              <div className="tw-grid tw-grid-cols-[1fr_auto] gap-3 w-full tw-items-center">
                <div className="tw-min-w-[220px]">
                  <Slider
                    value={percent}
                    onChange={(e) => handleEfficiencyChange(machine, (e.value as number) / 100)}
                    step={5}
                    min={0}
                    max={100}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-row justify-content-between mt-6">
        <Button
          label="戻る"
          icon="pi pi-arrow-left"
          iconPos="left"
          size="small"
          severity="secondary"
          onClick={handleBack}
        />
        <Button
          label="次へ"
          icon="pi pi-arrow-right"
          iconPos="right"
          size="small"
          severity="success"
          onClick={handleProceed}
        />
      </div>
    </div>
  );
};

export default CapacitySetting;
